import { useSeminarStore } from "../../stores/seminarStore";
import { queryClient } from "../../lib/queryClient";
import { seminarsQueryKey, useSeminarSelection } from "../queries/useSeminars";
import type { Seminar } from "../../types/entities";

// Stable "nothing selected" sentinel. Returning a fresh `{}` here on every render
// gives seminarInputs a new identity each time, which cascades through every
// downstream useMemo/useCallback/useEffect that lists it as a dependency (e.g.
// EmailModal's subject/body effect) and can drive an infinite render loop. A
// single frozen reference keeps those dependencies stable when no seminar is set.
// (`{} as Seminar` mirrors the invoiceStore's "no seminar selected" placeholder —
// a stable empty shape callers read defensively.)
const EMPTY_SEMINAR = Object.freeze({}) as Seminar;

/**
 * Look for a seminar in EVERY cached seminar list, not just the active one.
 *
 * The lists are cached per filter (['seminars', <params>]), so a seminar missing
 * from the active filtered list is often still sitting in another cached list —
 * server data we already hold, and a better answer than rendering blank.
 */
const findInSeminarCaches = (id: number): Seminar | undefined => {
  for (const [, list] of queryClient.getQueriesData<Seminar[]>({ queryKey: seminarsQueryKey })) {
    const hit = list?.find((seminar) => seminar.id === id);
    if (hit) return hit;
  }
  return undefined;
};

/**
 * The seminar object the seminar form/details should read.
 *
 * While an edit is in progress (editSeminar && editDraft has an id) the LIVE
 * selected seminar is the base and the mutable draft (a diff of only the fields
 * the user changed) is layered on top: keystrokes render immediately, while
 * server-derived collections (referent[], participations) stay live so a
 * mid-edit refetch surfaces without being masked by a stale snapshot. Otherwise
 * it resolves the currently-selected seminar from the active list query
 * (['seminars', activeSeminarQuery ?? null]) by selectedSeminarId.
 *
 * Resolution falls back once before giving up, because the active list is FILTERED
 * (upcoming-only by default) and the selected seminar can legitimately be absent
 * from it — most visibly right after saving an edit that pushes the seminar outside
 * the active filter, or while a filtered refetch is in flight. Returning the empty
 * sentinel then blanked every detail field and the seminar's own table, which reads
 * as data loss. So: the active list first, then any other cached seminar list.
 *
 * If the seminar is in no cache at all, the empty sentinel still stands. Closing
 * that last gap would need a real single-seminar fetch rather than another layer of
 * remembered state — a ref or module variable written during render is impure and
 * (rightly) rejected by the hooks lint rules.
 */
export const useSeminarInputs = (): Seminar => {
  const editSeminar = useSeminarStore((s) => s.editSeminar);
  const editDraft = useSeminarStore((s) => s.editDraft);
  const activeSeminarQuery = useSeminarStore((s) => s.activeSeminarQuery);
  const selectedSeminarId = useSeminarStore((s) => s.selectedSeminarId);

  const seminarSelection: Seminar[] = useSeminarSelection(activeSeminarQuery);

  const live: Seminar | undefined =
    selectedSeminarId == null
      ? undefined
      : (seminarSelection.find((s) => s.id === selectedSeminarId) ??
        findInSeminarCaches(selectedSeminarId));

  if (editSeminar && editDraft?.id) {
    return { ...(live ?? EMPTY_SEMINAR), ...editDraft };
  }

  return live ?? EMPTY_SEMINAR;
};
